import { Skeleton } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { ReactNode } from 'react';
import { Grafico, Nr1Card } from '../home/Home.styles';

const COLUMN_HEIGHTS = ['55%', '80%', '65%', '95%', '45%', '70%', '35%', '60%'];
const BAR_WIDTHS = ['92%', '84%', '76%', '70%', '62%', '55%', '48%', '40%', '34%', '28%'];
const LEGEND_WIDTHS = [72, 96, 60, 84, 68, 90];

type SkeletonDashboardChartVariant = 'pie' | 'columns' | 'bars' | 'gauge' | 'combo' | 'area';

interface ISkeletonDashboardChartProps {
  variant: SkeletonDashboardChartVariant;
  pieSize?: number;
  legendRows?: number;
  rows?: number;
  height?: number;
  compactTitle?: boolean;
  span?: 'half';
  legendBelow?: boolean;
  card?: 'nr1';
  area?: 'line' | 'gender';
}

const ChartHeader = ({ compact }: { compact?: boolean }) => (
  <>
    <Skeleton variant="text" width="60%" height={compact ? 22 : 28} />
    <Skeleton variant="text" width="80%" height={18} />
  </>
);

const Columns = ({ count, minHeight }: { count: number; minHeight: number }) => (
  <div style={{ flex: '1 1 auto', minHeight, display: 'flex', alignItems: 'flex-end', gap: 12, marginTop: 16 }}>
    {Array.from({ length: count }, (_, index) => (
      <Skeleton
        key={index}
        variant="rectangular"
        sx={{
          flex: '1 1 0',
          height: `${COLUMN_HEIGHTS[index % COLUMN_HEIGHTS.length]} !important`,
          maxWidth: 24,
          mx: 'auto',
          borderRadius: '4px 4px 0 0',
        }}
      />
    ))}
  </div>
);

const ChartCard = ({ card, span, area, children }: { card?: 'nr1'; span?: 'half'; area?: string; children: ReactNode }) => {
  const { palette } = useTheme();

  if (card === 'nr1') {
    return (
      <Nr1Card $isDark={palette.mode === 'dark'} aria-busy="true" data-area={area}>
        {children}
      </Nr1Card>
    );
  }

  return (
    <Grafico aria-busy="true" data-span={span}>
      {children}
    </Grafico>
  );
};

export const SkeletonDashboardChart = ({
  variant,
  pieSize = 160,
  legendRows = 4,
  rows = 6,
  height,
  compactTitle,
  span,
  legendBelow,
  card,
  area,
}: ISkeletonDashboardChartProps) => (
  <ChartCard card={card} span={span} area={area}>
    <div style={{ display: 'flex', flexDirection: 'column', flex: '1 1 auto', width: '100%', height }}>
      <ChartHeader compact={compactTitle} />

      {variant === 'pie' && (
        <div
          style={{
            flex: '1 1 auto',
            display: 'flex',
            flexDirection: legendBelow ? 'column' : 'row',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 16,
            marginTop: 16,
          }}
        >
          <div style={legendBelow ? { width: pieSize, maxWidth: '100%' } : { flex: `0 1 ${pieSize}px`, minWidth: 0 }}>
            <Skeleton variant="circular" width="100%" height="auto" sx={{ aspectRatio: '1' }} />
          </div>
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              gap: 8,
              ...(card === 'nr1' ? { flex: '1 1 auto', minWidth: 0, alignSelf: 'stretch', justifyContent: 'center' } : {}),
            }}
          >
            {Array.from({ length: legendRows }, (_, index) => (
              <div key={index} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <Skeleton variant="rectangular" width={card === 'nr1' ? 10 : 12} height={card === 'nr1' ? 10 : 12} sx={{ borderRadius: '2px' }} />
                <Skeleton variant="text" width={LEGEND_WIDTHS[index % LEGEND_WIDTHS.length]} height={16} />
                {card === 'nr1' && legendBelow && <Skeleton variant="text" width={36} height={16} sx={{ ml: 'auto' }} />}
              </div>
            ))}
          </div>
        </div>
      )}

      {variant === 'area' && (
        <Skeleton
          variant="rectangular"
          sx={{ flex: '1 1 auto', minHeight: 240, height: 'auto !important', mt: '16px', borderRadius: '4px' }}
        />
      )}

      {variant === 'columns' && <Columns count={6} minHeight={140} />}

      {variant === 'combo' && <Columns count={8} minHeight={250} />}

      {variant === 'bars' && (
        <div
          style={{
            flex: '1 1 auto',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'space-around',
            gap: 16,
            marginTop: 16,
          }}
        >
          {Array.from({ length: rows }, (_, index) => (
            <div key={index} style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
              <Skeleton variant="text" width="28%" height={16} sx={{ flexShrink: 0 }} />
              <Skeleton
                variant="rectangular"
                width={BAR_WIDTHS[index % BAR_WIDTHS.length]}
                height={24}
                sx={{ borderRadius: '0 6px 6px 0' }}
              />
            </div>
          ))}
        </div>
      )}

      {variant === 'gauge' && (
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', marginTop: 16 }}>
          <Skeleton
            variant="rectangular"
            width={234}
            height={117}
            sx={{ maxWidth: '100%', borderRadius: '117px 117px 0 0', my: '6px' }}
          />
          <Skeleton variant="text" width={90} height={22} sx={{ mt: '8px' }} />
        </div>
      )}
    </div>
  </ChartCard>
);
